// @ts-strict-ignore
import { ProductListQuery } from "@dashboard/graphql";
import { RelayToFlat } from "@dashboard/types";
import { richTextToPlainText } from "@dashboard/utils/richText/compat";
import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";

interface ProductListItemProps {
  product: RelayToFlat<ProductListQuery["products"]>[0];
  onClick: () => void;
  selectedChannelId?: string;
}

export const ProductListItem = ({ product, onClick, selectedChannelId }: ProductListItemProps) => {
  const thumbnail = product?.thumbnail?.url;

  // Get the channel listing for the selected channel or the first one
  const channel = product?.channelListings?.find(
    listing => listing.channel.id === selectedChannelId,
  ) ?? product?.channelListings?.[0];

  // Use channel.isPublished to determine if product is published or draft
  const isPublished = channel?.isPublished ?? false;

  const formatDate = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDescriptionText = (description: unknown) => richTextToPlainText(description);

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: "#fff",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        padding: "16px",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#d1d5db";
        e.currentTarget.style.backgroundColor = "#f9fafb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e5e7eb";
        e.currentTarget.style.backgroundColor = "#fff";
      }}
    >
      <Box display="flex" alignItems="center" gap={4}>
        {/* Product Image */}
        <Box
          __width="64px"
          __height="64px"
          borderRadius={3}
          overflow="hidden"
          backgroundColor="default2"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={product.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
          )}
        </Box>

        {/* Product Info */}
        <Box __flex={1} display="flex" flexDirection="column" gap={1}>
          <Text size={4} fontWeight="medium">
            {product.name}
          </Text>
          {(() => {
            const descriptionText = getDescriptionText(product.description);

            return descriptionText ? (
              <Text size={2} color="default2">
                {descriptionText.length > 100
                  ? `${descriptionText.substring(0, 100)}...`
                  : descriptionText}
              </Text>
            ) : null;
          })()}
          <Box display="flex" gap={3} alignItems="center">
            {product.category && (
              <>
                <Text size={2} color="default2">
                  {product.category.name}
                </Text>
                <Text size={2} color="default2">•</Text>
              </>
            )}
            {product.updatedAt && (
              <Text size={2} color="default2">
                Updated {formatDate(product.updatedAt)}
              </Text>
            )}
          </Box>
        </Box>

        {/* Status */}
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2} __minWidth="120px">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              paddingLeft: "8px",
              paddingRight: "8px",
              paddingTop: "4px",
              paddingBottom: "4px",
              borderRadius: "6px",
              backgroundColor: isPublished
                ? "rgba(59, 130, 246, 0.15)"
                : "rgba(251, 191, 36, 0.25)",
            }}
          >
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: isPublished
                  ? "rgb(59, 130, 246)"
                  : "rgb(251, 191, 36)",
              }}
            />
            <Text
              size={2}
              fontWeight="medium"
              style={{
                color: isPublished
                  ? "rgb(59, 130, 246)"
                  : "rgb(251, 191, 36)",
              }}
            >
              {isPublished ? "Published" : "Draft"}
            </Text>
          </div>
        </Box>
      </Box>
    </div>
  );
};
